import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { PlatformBrandingNotice } from "@/components/platform-landing/PlatformBrandingNotice";
import { PageHero } from "@/components/ui/PageHero";
import { Pill } from "@/components/ui/Pill";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { useSeo } from "@/lib/seo";
import { buildTenantUrl } from "@/shell/resolveShell";

/**
 * `/how-it-works` on the PLATFORM shell (``forgmatic.com``).
 *
 * The tenant shell mounts a different page at the same path
 * (``HowItWorks.tsx``) and that one is written for the person ordering
 * a part: "send **us** your files", "**we** agree the details", closing
 * CTA "upload a file". Mounted on the platform domain it made Forgmatic
 * read as the manufacturer rather than the software the manufacturer
 * runs (KAN-717) — the audience here is a shop owner evaluating the
 * platform, not their customer.
 *
 * The page still shows the buyer journey, because that is the reason
 * ``/how-it-works`` stayed mounted on this shell at all: an owner
 * wants to see what their own customer will walk through. It is framed
 * as exactly that — section 2 of three, explicitly "what YOUR customer
 * sees" — with the setup that precedes it and the order that follows.
 *
 * Deliberately absent, and the second half of KAN-717: the tenant page's
 * ``DeliveryModes`` block and SLA timeline. On the apex those resolve
 * against the fallback tenant, so the marketing site was advertising
 * one shop's delivery ladder as if it were the product ("fast — from 10
 * days" ranked above "standard — from 5 days", carrying the RECOMMENDED
 * badge) and explaining that "an admin configures the times and price
 * multipliers" to a visitor who is not an admin of anything yet.
 */
export default function PlatformHowItWorks(): JSX.Element {
  const { t } = useTranslation("saas");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;

  useSeo({
    title: t("platformHowItWorks.seo.title"),
    description: t("platformHowItWorks.seo.description"),
    path: "/how-it-works",
    lang,
  });

  return (
    <>
      <PageHero innerClassName="max-w-6xl px-7 pb-14 pt-12 md:pt-16">
        <Pill tone="accent">{t("platformHowItWorks.eyebrow")}</Pill>
        <h1 className="mt-4 max-w-3xl text-3xl font-semibold tracking-tight text-ink md:text-5xl">
          {t("platformHowItWorks.title")}
        </h1>
        <p className="mt-4 max-w-2xl text-base text-ink-2 md:text-lg">
          {t("platformHowItWorks.lead")}
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link
            to={`/${lang}/sign-up-shop`}
            className="inline-flex items-center rounded-md bg-ink px-5 py-2.5 text-sm font-medium text-bg shadow-sm transition-colors hover:bg-ink-2"
            data-testid="platform-how-it-works-cta-primary"
          >
            {t("platformHowItWorks.ctaPrimary")}
          </Link>
          <Link
            to={`/${lang}/pricing-plans`}
            className="inline-flex items-center rounded-md border border-border-strong bg-surface px-5 py-2.5 text-sm font-medium text-ink-2 transition-colors hover:bg-bg"
            data-testid="platform-how-it-works-cta-secondary"
          >
            {t("platformHowItWorks.ctaSecondary")}
          </Link>
        </div>
      </PageHero>

      <main className="mx-auto max-w-6xl px-7 pb-20 pt-14">
        <Stage
          stage="setup"
          itemKeys={["catalog", "rates", "capacity"]}
          t={t}
        />

        <Stage
          stage="customer"
          itemKeys={["upload", "price", "configure", "order"]}
          t={t}
          className="mt-20"
          // Four cards: a 3-column grid left "order and pay" alone on a
          // second row (KAN-1946). 2×2 on tablets, one row on desktop.
          gridClassName="sm:grid-cols-2 lg:grid-cols-4"
          // The plan names belong to the lead's sentence about your
          // logo and colours, not to a second note under the cards that
          // repeated it.
          afterLead={
            <>
              {" "}
              <PlatformBrandingNotice inline />
            </>
          }
        >
          {/* The one cross-host link on the page. The demo shop is a real
              tenant on its own subdomain — the whole point of the section
              is that the owner can go and look at the actual thing.

              buildtenanturl-ok: public storefront. The visitor is
              anonymous, so there is no session to carry across. */}
          <div className="mt-8 flex flex-col gap-3 rounded-lg border border-dashed border-border bg-surface p-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="m-0 max-w-xl text-sm text-ink-2">
              {t("platformHowItWorks.customer.note")}
            </p>
            <a
              href={buildTenantUrl(`/${lang}`)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex shrink-0 items-center gap-2 self-start rounded-md border border-border-strong bg-bg px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-surface-2 sm:self-auto"
              data-testid="platform-how-it-works-demo-link"
            >
              {t("platformHowItWorks.customer.demoCta")}
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </Stage>

        <Stage
          stage="shop"
          itemKeys={["inbox", "production", "paperwork"]}
          t={t}
          className="mt-20"
        />

        <section
          className="mt-20 rounded-xl border border-border bg-surface px-6 py-10 text-center"
          data-testid="platform-how-it-works-final-cta"
        >
          <h2 className="text-2xl font-semibold tracking-tight text-ink md:text-3xl">
            {t("platformHowItWorks.finalCta.heading")}
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-ink-2">
            {t("platformHowItWorks.finalCta.lead")}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to={`/${lang}/sign-up-shop`}
              className="inline-flex items-center rounded-md bg-ink px-5 py-2.5 text-sm font-medium text-bg shadow-sm hover:bg-ink-2"
            >
              {t("platformHowItWorks.finalCta.primary")}
            </Link>
            <Link
              to={`/${lang}/pricing-plans`}
              className="inline-flex items-center rounded-md border border-border-strong bg-surface px-5 py-2.5 text-sm font-medium text-ink-2 hover:bg-bg"
            >
              {t("platformHowItWorks.finalCta.secondary")}
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}

/** One of the three stages (set up → your customer → you), rendered as a
 *  numbered heading plus a card grid. ``children`` hangs extra content
 *  under the grid — stage 1 uses it for the starter-template note and
 *  stage 2 for the demo-shop link. */
function Stage({
  stage,
  itemKeys,
  t,
  className,
  gridClassName = "sm:grid-cols-2 lg:grid-cols-3",
  afterLead,
  children,
}: {
  stage: "setup" | "customer" | "shop";
  itemKeys: readonly string[];
  t: (key: string) => string;
  className?: string;
  gridClassName?: string;
  afterLead?: React.ReactNode;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <section className={className} data-testid={`platform-how-it-works-${stage}`}>
      <header className="max-w-3xl">
        <span className="mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          {t(`platformHowItWorks.${stage}.kicker`)}
        </span>
        <h2 className="mt-2 text-2xl font-semibold tracking-tight text-ink md:text-3xl">
          {t(`platformHowItWorks.${stage}.heading`)}
        </h2>
        <p className="mt-2 text-ink-2">
          {t(`platformHowItWorks.${stage}.lead`)}
          {afterLead}
        </p>
      </header>
      <div className={`mt-8 grid gap-6 ${gridClassName}`}>
        {itemKeys.map((key) => (
          <article
            key={key}
            className="rounded-lg border border-border bg-surface p-5"
            data-testid={`platform-how-it-works-${stage}-${key}`}
          >
            <h3 className="text-base font-semibold text-ink">
              {t(`platformHowItWorks.${stage}.items.${key}.title`)}
            </h3>
            <p className="mt-2 text-sm text-ink-2">
              {t(`platformHowItWorks.${stage}.items.${key}.body`)}
            </p>
          </article>
        ))}
      </div>
      {children}
    </section>
  );
}
